// How long a load has left, for its loading screen to say: from the
// requests it still waits on (steps.ts's `pace.left`), the time each has
// taken in this load so far, and, until this load has a pace of its own,
// the pace past loads of its kind kept in this browser. An estimate, said
// as a calm range ("about 10 s left") that counts down between answers and
// does not climb back for a little more work found, so it never flaps.

/** What past loads of a kind took: their first answer, and each request after it (ms). */
export interface Pace {
  firstMs: number;
  perRequestMs: number;
}

/** Until a kind has a pace: a guess at a GitHub GraphQL request's time. */
export const DEFAULT_PER_REQUEST_MS = 700;
/** How much a new load moves a kept pace (an exponentially weighted mean). */
export const PACE_WEIGHT = 0.3;
/** Below this the screen says nothing of time: it is about to go. */
export const SAY_FROM_MS = 2000;
/** The ranges said, in seconds; past the last, "over N minutes". */
export const STEPS_S = [5, 10, 15, 20, 30, 45, 60, 90, 120] as const;
/** A said range climbs only for an estimate this many times past it: more work found is not news. */
export const CLIMB = 1.5;

/** What a load has shown of its pace: when it began, and when its first answer sized it. */
export interface Track {
  start: number;
  /** When `left` was first known, and what it was then; null until then. */
  sized: { at: number; left: number } | null;
  /** When `left` last changed, and to what. */
  last: { at: number; left: number } | null;
}

export const track = (start: number): Track => ({ start, sized: null, last: null });

/** The track with what the load says now (`left` requests, null: not sized yet), at `now`. */
export function observe(t: Track, left: number | null, now: number): Track {
  if (left === null) return t;
  const sized = t.sized ?? { at: now, left };
  const last = t.last && t.last.left === left ? t.last : { at: now, left };
  return sized === t.sized && last === t.last ? t : { ...t, sized, last };
}

/**
 * The time left (ms), or null where nothing can be said: before the first
 * answer, what past first answers took less the time gone; after it, the
 * requests left at this load's own rate since its first answer (the kept
 * pace's until a request has been answered), less the time since the last
 * answer, so it counts down between answers.
 */
export function remainingMs(t: Track, left: number | null, now: number, pace: Pace | null): number | null {
  if (left === null || !t.sized || !t.last) return pace ? Math.max(0, pace.firstMs - (now - t.start)) : null;
  const answered = t.sized.left - t.last.left;
  const per = answered > 0 ? (t.last.at - t.sized.at) / answered : (pace?.perRequestMs ?? DEFAULT_PER_REQUEST_MS);
  return Math.max(0, left * per - (now - t.last.at));
}

/**
 * What the screen says, in seconds (a value of STEPS_S, or Infinity for
 * "over" its last): nothing under SAY_FROM_MS or unknown; down a step as
 * soon as the estimate is; up only when it is CLIMB times past what is said.
 */
export function said(prev: number | null, ms: number | null): number | null {
  if (ms === null || ms < SAY_FROM_MS) return null;
  const s = ms / 1000;
  const next = STEPS_S.find((x) => x >= s) ?? Infinity;
  if (prev === null || next <= prev) return next;
  return s > prev * CLIMB ? next : prev;
}

/** `said`'s value in words. */
export function inWords(s: number): string {
  if (s === Infinity) return `over ${STEPS_S.at(-1)! / 60} minutes left`;
  if (s === 60) return 'about a minute left';
  if (s === 90) return 'about a minute and a half left';
  return s > 60 ? `about ${s / 60} minutes left` : `about ${s} s left`;
}

/** The pace a finished load leaves: its own, folded into what was kept. */
export function settle(kept: Pace | null, t: Track, end: number): Pace | null {
  if (!t.sized || !t.last) return kept;
  const firstMs = t.sized.at - t.start;
  // Every request it waited on past its first answer was answered by `end`.
  const own: Pace = { firstMs, perRequestMs: t.sized.left > 0 ? (end - t.sized.at) / t.sized.left : (kept?.perRequestMs ?? DEFAULT_PER_REQUEST_MS) };
  if (!kept) return own;
  const mix = (a: number, b: number) => a + PACE_WEIGHT * (b - a);
  return { firstMs: mix(kept.firstMs, own.firstMs), perRequestMs: mix(kept.perRequestMs, own.perRequestMs) };
}

const KEY = 'changelist:load-pace';

/** The pace kept for loads of `kind`; null when none is, or storage is out of reach. */
export function keptPace(kind: string): Pace | null {
  try {
    const all = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, Pace>;
    const p = all[kind];
    return p && Number.isFinite(p.firstMs) && Number.isFinite(p.perRequestMs) ? p : null;
  } catch {
    return null;
  }
}

/** Keeps `pace` for loads of `kind`, where storage allows. */
export function keepPace(kind: string, pace: Pace): void {
  try {
    const all = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, Pace>;
    localStorage.setItem(KEY, JSON.stringify({ ...all, [kind]: pace }));
  } catch {
    // Private windows and blocked storage: the next load guesses again.
  }
}
